import { Product } from "../types/store";

export const HOME_PAGE_SECTION_TAGS = [
  "hard-to-find-products",
  "everyday-essentials",
  "electrical-essentials",
  "hardware-tools",
  "plumbing-bathroom",
] as const;

const SECTION_TAG_MATCHERS: Record<string, string[]> = {
  "hard-to-find": ["hard-to-find-products"],
  "everyday-essentials": ["everyday-essentials"],
  "electrical-essentials": ["electrical-essentials"],
  "hardware-tools": ["hardware-tools"],
  "plumbing-bathroom": ["plumbing-bathroom"],
};

const SECTION_CATEGORY_MATCHERS: Record<string, string[]> = {
  "electrical-essentials": ["electrical", "electric", "wiring", "cable", "wire"],
  "hardware-tools": ["hardware", "tool", "tools", "drill", "fastener"],
  "plumbing-bathroom": ["plumbing", "bathroom", "bath", "sanitary", "pipe", "fitting"],
};

const normalize = (value: string) => value.trim().toLowerCase();

const matchesKeywords = (product: Product, keywords: string[]) => {
  if (!keywords.length) {
    return false;
  }

  const haystack = [
    product.category,
    product.categorySlug,
    product.subcategory,
    product.subcategorySlug,
    product.name,
    ...(product.tags || []),
  ]
    .map(normalize)
    .join(" ");

  return keywords.some((keyword) => haystack.includes(normalize(keyword)));
};

export const getHomepageSectionProducts = (products: Product[], sectionKey: string) => {
  const normalizedSectionKey = normalize(sectionKey);

  if (normalizedSectionKey === "recently-added") {
    return [...products].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    );
  }

  if (normalizedSectionKey === "best-selling") {
    return [...products].sort((left, right) => {
      if (right.bestSeller !== left.bestSeller) {
        return Number(right.bestSeller) - Number(left.bestSeller);
      }

      if (right.featured !== left.featured) {
        return Number(right.featured) - Number(left.featured);
      }

      return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
    });
  }

  const tagMatchers = SECTION_TAG_MATCHERS[normalizedSectionKey] || [];
  const categoryMatchers = SECTION_CATEGORY_MATCHERS[normalizedSectionKey] || [];

  if (normalizedSectionKey === "plumbing-bathroom") {
    return products.filter((product) => {
      return (
        tagMatchers.some((tag) => product.tags.some((productTag) => normalize(productTag) === tag)) ||
        matchesKeywords(product, categoryMatchers)
      );
    });
  }

  if (categoryMatchers.length) {
    return products.filter(
      (product) =>
        tagMatchers.some((tag) => product.tags.some((productTag) => normalize(productTag) === tag)) ||
        matchesKeywords(product, categoryMatchers),
    );
  }

  if (tagMatchers.length) {
    return products.filter((product) =>
      tagMatchers.some((tag) => product.tags.some((productTag) => normalize(productTag) === tag)),
    );
  }

  return [];
};

export const isHomepageSectionKey = (value: string) =>
  Object.prototype.hasOwnProperty.call(SECTION_TAG_MATCHERS, normalize(value)) ||
  ["recently-added", "best-selling"].includes(normalize(value));
